import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { IconButton } from '@mui/material';
import { CancelRounded } from '@mui/icons-material';
import VideoPlayer from 'src/shared/components/video-player/components/video-player';

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  playbackId: string;
  playbackToken: string;
}

export const VideoModal: React.FC<VideoModalProps> = ({
  isOpen,
  onClose,
  playbackId,
  playbackToken,
}) => {
  // Generate a unique player ID for this modal
  const videoPlayerId = `modal-${playbackId}`;

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [isOpen, onClose]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-90"
      onClick={handleBackdropClick}
      style={{ backdropFilter: 'blur(3px)' }}
    >
      <div className="relative w-full max-w-[90%] md:max-w-[85%] lg:max-w-[75%] xl:max-w-[1200px] aspect-video bg-black rounded-lg overflow-hidden">
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: 'white',
            zIndex: 10,
            backgroundColor: 'rgba(0,0,0,0.3)',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
          }}
          aria-label="Close"
        >
          <CancelRounded />
        </IconButton>

        <div className="w-full h-full">
          <VideoPlayer
            playbackId={playbackId}
            playbackToken={playbackToken}
            videoId={playbackId}
            simpleControls={true}
            playerId={videoPlayerId}
            handleAspectRatio={true}
          />
        </div>
      </div>
    </div>
  );

  // Create portal to render modal at root level
  return createPortal(modalContent, document.body);
};
